import AwesomeDebouncePromise from "awesome-debounce-promise";
import classNames from "classnames";
import { SERVER_URL } from "Constants";
import saveAs from "file-saver";
import { getSearchRequests, SearchRequestListItem } from "Hooks/Api/apiSearchRequest";
import { processingStatusOptions } from "Models/Enums";
import { OrganisationHelper } from "Models/Helpers/OrganisationHelper";
import { store } from "Models/Store";
import moment from "moment";
import { useMemo, useState } from "react";
import { DEFAULT_PAGE_LENGTH } from "Util/Constants";
import { buildBlob } from "Util/CsvUtils";
import { Button, Colors, Display } from "../Button/Button";
import MultiComboboxSetter from "../Combobox/MultiComboboxSetter";
import { DateRangePicker } from "../DateRangePicker/DateRangePicker";
import EntityList, { IEntityListHeaderProps } from "../EntityList/EntityList";
import TablePagination from "../Pagination/TablePagination";
import RequestWrap, { GetRefreshKey } from "../RequestWrap/RequestWrap";
import { TextFieldSetter } from "../TextBox/TextFieldSetter";
import { MotorVehicleSearchGenerateWorkflow } from "./MotorVehicleSearchGenerateWorkflow";
import { MotorVehicleSearchGenerateWorkflowProvider } from "./MotorVehicleSearchReducers";
import SearchRequestStatusBox, { getProcessingStatusDisplayName } from "./SearchRequestStatusBox";
import { PaginatedResponse } from "Types/PaginatedResponse";

const fetchSearchRequestListData = AwesomeDebouncePromise(async (
	search: string,
	sortColumn: string,
	sortDescending: boolean,
	businessEntityIds: string[],
	page: number,
	dateRange: Date[],
	noPagination?: boolean
): Promise<PaginatedResponse<SearchRequestListItem>> => {
	const response = await getSearchRequests({
		searchBy: search,
		sortBy: sortColumn,
		sortDescending: sortDescending,
		businessEntityIds: businessEntityIds,
		page: page,
		dateRange: dateRange,
		noPagination: noPagination
	});
	return response.data;
},
300,
{ leading: true });

export function MotorVehicleSearchRequestContent(props: { showSearchModal?: boolean }) {
	const {
		showSearchModal = false
	} = props;

	const [showGenerateSearch, setShowGenerateSearch] = useState<boolean>(showSearchModal);
	const [search, setSearch] = useState('');
	const [sortColumn, setSortColumn] = useState('date');
	const [sortDescending, setSortDescending] = useState(true);
	const [page, setPage] = useState(0);
	const [businessEntityIds, setBusinessEntityIds] = useState([] as string[]);
	const [showFilters, setShowFilters] = useState(false);
	const [dateRange, setDateRange] = useState({ value: [] } as { value: Date[] });
	const [triggerUpdate, setTriggerUpdate] = useState(0); // Increment to force the user list to refresh

	const onSortChange = (header: string) => {
		setSortDescending(header === sortColumn ? !sortDescending : false);
		setSortColumn(header);
	};

	const getColumns = (): IEntityListHeaderProps<SearchRequestListItem>[] => {
		return [
			{
				columnName: "id",
				displayName: "ID",
				value: item => item.id,
				hide: true
			},
			{
				displayName: "Date & Time",
				columnName: "date",
				value: item => moment(item.date).format('HH:mm DD/MM/YYYY'),
				sortable: true,
				sortClicked: () => onSortChange('date')
			},
			{
				displayName: "Bill To",
				columnName: "billTo",
				value: item => item.billTo,
				sortable: true,
				sortClicked: () => onSortChange('billTo')
			},
			{
				displayName: "Search Criteria",
				columnName: "searchCriteria",
				value: item => item.searchCriteria,
				sortable: true,
				sortClicked: () => onSortChange('searchCriteria')
			},
			{
				displayName: "Product",
				columnName: "productName",
				value: item => item.productName,
				sortable: true,
				sortClicked: () => onSortChange('productName')
			},
			{
				displayName: "Price Inc GST",
				columnName: "price",
				value: item => `$${item.price?.toFixed(2) || '-'}`,
				sortable: true,
				sortClicked: () => onSortChange('price')
			},
			{
				displayName: "Generated By",
				columnName: "generatedBy",
				value: item => item.generatedBy,
				sortable: true,
				sortClicked: () => onSortChange('generatedBy')
			},
			{
				displayName: "Status",
				columnName: "status",
				value: item => <SearchRequestStatusBox status={item.status} />,
				sortable: true,
				sortClicked: () => onSortChange('status')
			},
			{
				columnName: "downloadLink",
				displayName: "",
				value: item => (
					<>
						{processingStatusOptions[item.status] === processingStatusOptions.PROCESSED && item.reportId && (
							<Button
								icon={{ icon: 'arrow-right-up', iconPos: 'icon-right' }}
								display={Display.Text}
								colors={Colors.Primary}
								onClick={() => { window.open(`${SERVER_URL}/api/files/${item.reportId}`) }}
							>
								View
							</Button>
						)}
						{processingStatusOptions[item.status] === processingStatusOptions.ERROR && (
							<Button
								className="link-item contact-support"
								display={Display.Text}
								colors={Colors.Error}
								onClick={() => { window.open("mailto:support@accessintell.com", "_self") }}
							>
								Contact Support
							</Button>
						)}
					</>
				),
				sortable: false
			},
		];
	}

	const fetchVehicleSearchForExports = async () => {
		const response = await fetchSearchRequestListData(
			search,
			sortColumn,
			sortDescending,
			businessEntityIds,
			page,
			dateRange.value,
			true
		);
		const searchResults = response.data;

		const result = [] as string[][];
		const columns = [
			{
				displayName: "Date & Time",
				value: (item: SearchRequestListItem) => moment(item.date).format('HH:mm DD/MM/YYYY'),
			},
			{
				displayName: "Billed To",
				value: (item: SearchRequestListItem) => item.billTo,
			},
			{
				displayName: "Search Criteria",
				value: (item: SearchRequestListItem) => item.searchCriteria.replace(/"/gi, '""'),
			},
			{
				displayName: "Product",
				value: (item: SearchRequestListItem) => item.productName,
			},
			{
				displayName: "Price Inc GST",
				value: (item: SearchRequestListItem) => `$${item.price?.toFixed(2) || '-'}`,
			},
			{
				displayName: "Generated By",
				value: (item: SearchRequestListItem) => item.generatedBy,
			},
			{
				displayName: "Status",
				value: (item: SearchRequestListItem) => getProcessingStatusDisplayName(item.status),
			}
		]

		const headers = columns.map(column => column.displayName?.toString() ?? '');
		result.push(headers);
		searchResults.forEach(searchResult => {
			result.push(
				columns.map(column => column.value(searchResult)?.toString() ?? ''),
			);
		});

		return result;
	};

	const refreshKey = useMemo(() => {
		return GetRefreshKey(
			search,
			sortColumn,
			sortDescending,
			businessEntityIds,
			page,
			dateRange.value,
			triggerUpdate,
			store?.getUser?.userType === 'SUPER_USER' ? store.impersonatingOrganisationId : ''
		)
	}, [search, sortColumn, sortDescending, page, dateRange, triggerUpdate, businessEntityIds, store.impersonatingOrganisationId]);

	const resetFilters = () => {
		setDateRange({ value: [] });
		setBusinessEntityIds([]);
	}
	return (
		<div className="credit-report-list">
			<div className="search-container">
				<div className="searchbar search">
					<TextFieldSetter
						className={classNames('search-input search__collection')}
						value={search}
						setValue={setSearch}
						onAfterChange={() => setPage(0)}
						label="A search for entities"
						labelVisible={false}
						placeholder="Search Rego, VIN or State"
					/>
					<Button
						className={classNames('filter-toggle-btn', { active: showFilters })}
						icon={{ icon: 'filter-2', iconPos: 'icon-left' }}
						colors={Colors.Primary}
						display={showFilters ? Display.Solid : Display.Outline}
						onClick={() => {
							setShowFilters(show => !show);
							resetFilters();

						}}
					>
						Filter By
					</Button>
				</div>
				<div className="actions">
					<Button
						display={Display.Solid}
						colors={Colors.Primary}
						onClick={() => { setShowGenerateSearch(true) }}
						icon={{ icon: 'plus', iconPos: 'icon-left' }}
					>
						Generate Search
					</Button>
					<Button
						display={Display.Outline}
						colors={Colors.Primary}
						onClick={async () => {
							const exportCreditReports = await fetchVehicleSearchForExports();
							const blob = buildBlob(exportCreditReports, ',', '"');
							saveAs(blob, `exported-search-requests-${moment().format('YYYY-MM-DD')}.csv`);
						}}
					>
						Export CSV
					</Button>
				</div>

				{showFilters && (
					<div className="search-filter-section">
						<DateRangePicker
							className="date-range-picker"
							model={dateRange}
							modelProperty="value"
							placeholder="Filter by date range"
							flatpickrOptions={{
								formatDate: (date: Date) => moment.utc(date).toDate().toLocaleDateString(),
								maxDate: moment().utc().toDate(),
							}}
							dateFormat="DD/MM/YYYY"
							onAfterChange={value => {
								setDateRange({ value: value });
								setPage(0);
							}}
						>
						</DateRangePicker>
						<MultiComboboxSetter
							className="business-entity-filter"
							value={businessEntityIds}
							setValue={setBusinessEntityIds}
							getOptionValue={(value?: string) => value}
							placeholder="Business entities"
							label=""
							labelVisible={false}
							searchable
							onAfterSet={() => setPage(0)}
							options={OrganisationHelper.getBusinessOptions(store.getUser?.organisation)}
						/>
					</div>
				)}
			</div>

			<div className="collection-component credit-reports">
				<RequestWrap
					request={() => fetchSearchRequestListData(
						search,
						sortColumn,
						sortDescending,
						businessEntityIds,
						page,
						dateRange.value,
					)}
					refreshKey={refreshKey}
				>
					{request => {
						const data = request.data;
						return (
							<>
								{data?.length === 0 && <h6>No search requests found.</h6>}
								{data?.length > 0 && (
									<>
										<EntityList
											collection={data}
											columns={getColumns()}
											idColumn="id"
											sortColumn={sortColumn}
											sortDescending={sortDescending}
										/>
										<section className="collection__load">
											<TablePagination
												perPage={DEFAULT_PAGE_LENGTH}
												pageNo={page}
												totalRecords={request.totalCount}
												onPageChange={setPage}
											/>
										</section>
									</>
								)}
								{/* Tech debt: avoiding multiple overlayed spinners. Need to do use in line request instead of RequestWrapper */}
								{showGenerateSearch && (
									<MotorVehicleSearchGenerateWorkflowProvider>
										<MotorVehicleSearchGenerateWorkflow onClose={success => {
											setShowGenerateSearch(false);
											if (success) setTriggerUpdate(prev => prev + 1);
										}} />
									</MotorVehicleSearchGenerateWorkflowProvider>
								)}
							</>
						)
					}}
				</RequestWrap>
			</div>
		</div>
	)
}